import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom"; 
import api from "../services/api";
import jalaali from "jalaali-js"; 
import { useTasks } from "../context/TasksContext";

export default function Home() {
  const { refreshTasks } = useTasks();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleStartTask = async (taskId) => {
    try {
        await api.post(`work-logs/${taskId}/start_task/`);
        
        refreshTasks();
        
        fetchDashboard();
    } catch (error) {
        console.error("Error", error);
    }
  };

  // --- لیست نام ماه‌های شمسی ---
  const persianMonths = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
  ];

  // --- دریافت اطلاعات داشبورد ---
  const fetchDashboard = async () => {
    try {
      const res = await api.get("dashboard/");
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // --- هندل کردن ساعت کاری ---
  const handleClock = async () => {
      if (!data) return;
      try {
        if (data.attendance_status === "out") {
          await api.post("attendance/clock_in/");
        } else {
          await api.post("attendance/clock_out/");
        }
        fetchDashboard();
      } catch (error) { 
          console.error(error);
          // نمایش پیام خطای سرور به کاربر
          if (error.response && error.response.data && error.response.data.detail) {
              alert(error.response.data.detail);
          } else {
              alert("خطا در ثبت ساعت کاری");
          }
      }
  };

  // --- هندل کردن پایان تسک ---
  const handleFinishTask = async (taskId) => {
    if (!window.confirm("آیا پایان این کار را تایید می‌کنید؟")) return;
    try {
      await api.post(`work-logs/${taskId}/finish_task/`, { status: 'done' });
      fetchDashboard();
    } catch (error) { console.error(error); }
  };

  // --- دکمه هوشمند ساعت کاری ---
  const renderClockButtonContent = () => {
    if (!data) return <span>در حال بارگذاری...</span>;

    // 1. حالت در حال کار (Active)
    if (data.attendance_status === "in") {
      return (
        <>
           {/* آیکون قرمز برای توقف */}
           <svg><use xlinkHref="#bbf3a9" /></svg>
           <span>پایان کار</span>
           {/* نمایش انیمیشن یا متن در حال اجرا */}
           <bdi style={{ color: '#0aaf33', fontSize: '0.85rem', marginTop:'4px' }}>
              <span className="blink-dot">●</span> در حال کار هستید...
           </bdi>
        </>
      );
    }

    // 2. حالت خارج شده اما امروز کار کرده (Paused/Stopped)
    if (data.attendance_status === "out" && data.total_seconds > 0) {
      return (
        <>
           {/* آیکون سبز برای شروع مجدد */}
           <svg><use xlinkHref="#b26965" /></svg>
           <span>شروع دوباره کار</span>
           
           <div style={{ display:'flex', flexDirection:'column', lineHeight:'1.2',textAlign:'right' }}>
               <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight:'bold' }}>
                  خسته نباشید
               </span>
               
               <bdi style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>
                   کارکرد امروز: {data.total_worked_today}
               </bdi>
           </div>
        </>
      );
    }

    return (
      <>
        <svg><use xlinkHref="#tagIcon" /></svg>
        <span>شروع ساعت کاری</span>
      </>
    );
  };

  const nowText = useMemo(() => {
    try {
      const d = new Date();
      const dateFormatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian-nu-latn", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
      const parts = dateFormatter.formatToParts(d);
      const get = (type) => parts.find((p) => p.type === type)?.value;
      const time = d.toLocaleTimeString("fa-IR-u-nu-latn", { hour: "2-digit", minute: "2-digit" });
      return `امروز ${get("weekday")} ${get("day")} ${get("month")} ${get("year")} ساعت ${time}`;
    } catch { return "امروز"; }
  }, []);

  // --- تابع محاسبه تاریخ شمسی برای Aria Label ---
  const getHeatmapDate = (index) => {
      const today = new Date();
      const jToday = jalaali.toJalaali(today);
      const currentYear = jToday.jy;

      // ساخت تاریخ 1 فروردین سال جاری میلادی
      const startOfYearGregorian = jalaali.toGregorian(currentYear, 1, 1);
      const gDate = new Date(startOfYearGregorian.gy, startOfYearGregorian.gm - 1, startOfYearGregorian.gd);
      
      // افزودن تعداد روزهای گذشته (اینکس)
      gDate.setDate(gDate.getDate() + index);
      
      // تبدیل مجدد به شمسی
      const jDate = jalaali.toJalaali(gDate);
      
      // دریافت نام ماه
      const monthName = persianMonths[jDate.jm - 1]; 

      // فرمت خروجی: 1403/بهمن/10
      return `${jDate.jd}-${monthName}-${jDate.jy}`;
  };

  const heatmapGrid = useMemo(() => {
    if (data?.heatmap && data.heatmap.length > 0) return data.heatmap;
    return Array.from({ length: 365 }).fill("day-show-level-1");
  }, [data?.heatmap]);

  const monthly = [
    { name: "رضا کماسی", value: 70 },
    { name: "عرفان سلطانی نسب", value: 90, me: true },
  ];

  if (loading) return null;

  const activeDoingTasks = data?.active_tasks?.filter(task => task.status === 'doing') || [];

  return (
    <>
      <div className="container">
        <div className="row p-full">
          <div className="col-12">
            <div className="panel-wrapper">
              
              {/* Box 1: گزارش روز */}
              <div className="panel-box panel-box__4">
                <h2>گزارش روز</h2>
                <section>
                  <div className="panel-box__time"><span>{nowText}</span></div>
                  <div>
                    <button className="reset-btn" type="button" onClick={handleClock}>
                      {renderClockButtonContent()}
                    </button>

                    {activeDoingTasks.length > 0 ? (
                        activeDoingTasks.map((task) => (
                          <div className="panel-box__item" key={task.id}>
                            <svg><use xlinkHref="#articleIcon" /></svg>
                                <strong>
                                    <span>
                                        {task.project_title ? task.project_title : "پروژه"} :
                                    </span>{' '}
                                    {task.title}
                                </strong>
                                <bdi style={{opacity:"0.7"}}>
                                    شروع: {new Date(task.start_time).toLocaleTimeString("fa-IR", {hour: "2-digit", minute: "2-digit"})}
                                </bdi>
                            <button type="button" onClick={() => handleFinishTask(task.id)}>پایان پروژه</button>
                          </div>
                        ))
                    ) : (
                        <div className="panel-box__item"><strong>هیچ کار در حال انجامی ندارید</strong></div>
                    )}
                  </div>
                </section>
              </div>

              {/* Box 2: لیست همکاران (اکنون باید نمایش داده شود) */}
              <div className="panel-box panel-box__6">
                <div className="panel-table">
                  <section><div><p>همکار</p><p>وضعیت روزانه</p></div></section>
                  <section>
                    {data?.colleagues.map((col, index) => (
                      <div key={index}>
                        <p>{col.name}</p>
                        <p>
                          {col.daily_status || "-"}
                          <span>
                            {col.phone && col.phone !== "-" ? (
                                <a href={`tel:${col.phone}`} title={`تماس با ${col.phone}`}><svg><use xlinkHref="#phoneIcon" /></svg></a>
                            ) : <a style={{opacity:0}}><svg><use xlinkHref="#phoneIcon" /></svg></a>}
                            <Link to={`/chatroom/${col.username || col.id}`} title="ارسال پیام"><svg><use xlinkHref="#msgIcon" /></svg></Link>
                          </span>
                        </p>
                      </div>
                    ))}
                    {data?.colleagues.length === 0 && <p style={{padding:'10px'}}>همکار دیگری یافت نشد.</p>}
                  </section>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      
      {/* Box 3: هیت‌مپ اصلاح شده */}
      <div className="container">
        <div className="row p-full">
            <div className="col-12">
                <div className="day-show-wrapper">
                    <h1>روزهای کاری سال جاری</h1>
                    <section>
                        <p className="day-show-level-5">بیشتر {">"} 10</p>
                        <p className="day-show-level-4">10 {">"} تا {">"} 7</p>
                        <p className="day-show-level-3">7 {">"} تا {">"} 4</p>
                        <p className="day-show-level-2">4 {">"} تا {">"} 1</p>
                        <p className="day-show-level-1">خالی</p>
                    </section>
                    <div className="day-show">
                        {heatmapGrid.map((cls, i) => (
                            <span 
                                key={i} 
                                className={cls} 
                                aria-label={getHeatmapDate(i)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Box 4: نمودار */}
      {/* <div className="container">
        <div className="row p-full">
            <div className="col-12"><div className="tabel-monthly"><h1>میزان بازدهی</h1><section>{monthly.map((m)=><div key={m.name} className={m.me?"your-last-monthly":""}><span style={{"--last-month": m.value}}/></div>)}</section><section>{[100,90,80,70,60,50,40,30,20,10,0].map(n=><span key={n}>{n}</span>)}</section><section>{monthly.map(m=><div key={m.name}><p>{m.name}</p></div>)}</section><section></section></div></div>
        </div>
      </div> */}
    </>
  );
}